import { BRAND_PERSON_NAME, BRAND_SOURCE_KEY } from "@/lib/brandConfig";
/**
 * Invitación Exclusiva Argentina
 *
 * Página de invitación VIP exclusiva para Argentina con:
 * - Código de invitación único por URL
 * - Sistema de expiración configurable
 * - Diseño premium estilo BioLink
 * - Integración con Mercado Pago (payment gateway para Argentina)
 * - CTA URL configurable via API layout (cta_url) con fallback a Mercado Pago
 */

import MuxVideoPlayerWithCTA from "@/components/MuxVideoPlayerWithCTA";
import { getArInvitacionAdminCode } from "@/config/adminInvitacion";
import { AR_INVITACION_CURRENCY, AR_PLANS, AR_PLAN_STANDARD_SUBTITLE, AR_PLAN_VIP_SUBTITLE, AR_VIP_EXCLUSIVE_PATH, type ArPlanTier } from "@/config/arInvitacionPlans";
import { getChannelInvitePlaybackIdForAR, getChannelInviteVipPlaybackIdForAR } from "@/config/telegramGroupsVideo";
import { isApiUrlConfigured } from "@/config/apiUrl";
import { isVipPlanCardAvailable } from "@/config/vipConfig";
import { BANNER_IMAGE_URL, HEADER_PROFILE_IMAGE_URL } from "@/config/cloudflare";
import {
  AlertCircle,
  CheckCircle,
  Clock,
  Crown,
  ExternalLink,
  Gift,
  Loader2,
  Lock,
  Sparkles,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { mirrorFunnelEvent } from "@/lib/mirrorFunnelEvent";
import { fetchPremiumInvitePublicConfig } from "@/lib/premiumInvitePublicConfig";
import {
  validateInvitationValue,
  type InvitationValidateApiResponse,
  type InvitationValidationMode,
} from "@/lib/api";

type PlanTier = ArPlanTier;

/** URL de Telegram para redirect después del pago exitoso */
const TELEGRAM_SUCCESS_URL = "https://t.me/+8NXL4D2eaK8xYjUx";

/**
 * CTA fallback para la invitación AR — antes apuntaba a una suscripción MercadoPago
 * (preapproval_plan_id). Ahora MercadoPago está deshabilitado para membresías; la
 * ruta interna /exclusive-ar?tier=vip dispara el flujo Stripe con video Mux + checkout.
 */
const SUBSCRIPTION_URL_AR_FALLBACK = "/exclusive-ar?tier=vip";

/** Font iOS (San Francisco) para template de invitación */
const INVITATION_FONT_FAMILY =
  '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", "Helvetica Neue", Helvetica, Arial, sans-serif';

// Configuración de invitaciones
interface InvitationConfig {
  code: string;
  expiresAt: Date;
  maxUses: number;
  currentUses: number;
  discount?: number; // Porcentaje de descuento
  tier: "standard" | "vip" | "ultra_vip";
  createdBy?: string;
  metadata?: Record<string, unknown>;
}

import { BRAND_FAN_URL } from "@/lib/brandConfig";
import { getDatafastAttributionPayload } from "@/lib/datafast";

// API Response: GET {BRAND_FAN_URL}/api/invitation-codes/validate?ref=XXX | ?code=XXX
// Contrato: docs/LAYOUT_JSON_INVITACION.md
// Layout: title, subtitle, message, theme, cta_text, cta_url, image_url, background_image_url, spots_remaining, max_spots
// Aliases camelCase: imageUrl, backgroundImageUrl, ctaUrl. Formatos: JPEG, PNG, WebP, GIF, AVIF.
// También soporta: background_url (alias corto de background_image_url), backgroundUrl
type ValidateAPIResponse = InvitationValidateApiResponse;

// Fallback códigos para cuando la API no esté disponible
const FALLBACK_CODES: Record<string, InvitationConfig> = {
  ANA2024: {
    code: "ANA2024",
    expiresAt: new Date("2026-12-31T23:59:59"),
    maxUses: 100,
    currentUses: 45,
    discount: 20,
    tier: "vip",
  },
  ARGENTINA: {
    code: "ARGENTINA",
    expiresAt: new Date("2026-06-30T23:59:59"),
    maxUses: 50,
    currentUses: 12,
    discount: 15,
    tier: "standard",
  },
  ULTRAVIP: {
    code: "ULTRAVIP",
    expiresAt: new Date("2026-03-31T23:59:59"),
    maxUses: 10,
    currentUses: 3,
    discount: 30,
    tier: "ultra_vip",
  },
  BSAS: {
    code: "BSAS",
    expiresAt: new Date("2026-12-31T23:59:59"),
    maxUses: 200,
    currentUses: 0,
    discount: 10,
    tier: "standard",
  },
};

// Resultado unificado: una sola petición a validate retorna todo (valid, layout, tiempo).
type ValidateResult =
  | {
      valid: true;
      invitation: InvitationConfig;
      layout?: ValidateAPIResponse["layout"];
      validUntil: Date;
      validForSeconds: number;
    }
  | { valid: false; message: string };

// Una sola petición a /api/invitation-codes/validate. Soporta ?ref= y ?code=.
async function validateCodeViaAPI(
  code: string,
  urlParam: InvitationValidationMode,
): Promise<ValidateResult> {
  if (!isApiUrlConfigured()) {
    const fallback = FALLBACK_CODES[code.toUpperCase()];
    if (fallback) {
      const expiry = new Date(fallback.expiresAt);
      return {
        valid: true,
        invitation: fallback,
        validUntil: expiry,
        validForSeconds: Math.max(0, Math.floor((expiry.getTime() - Date.now()) / 1000)),
      };
    }
    return { valid: false, message: "Invitación no válida" };
  }

  try {
    const data: ValidateAPIResponse = await validateInvitationValue(code, urlParam);
    const valid = data.valid === true;

    const debug =
      typeof window !== "undefined" &&
      /\bdebug=1\b/.test(window.location.search);
    
    if (import.meta.env.DEV || debug) {
      console.log("[InvitacionExclusivaArgentina] validate response:", {
        mode: urlParam,
        code,
        valid,
        data,
        layout: data.layout,
        layoutType: typeof data.layout,
        layoutKeys: data.layout ? Object.keys(data.layout) : null,
      });
    }

    if (!valid) {
      return {
        valid: false,
        message:
          data.message?.trim() ||
          data.error?.trim() ||
          "Esta invitación ha alcanzado el límite máximo de usos o tu sesión ha expirado.",
      };
    }

    // valid: true
    const validUntil = data.valid_until_iso
      ? new Date(data.valid_until_iso)
      : new Date(Date.now() + 24 * 60 * 60 * 1000);
    const validForSeconds =
      data.valid_for_seconds ??
      Math.max(0, Math.floor((validUntil.getTime() - Date.now()) / 1000));

    const remaining = data.uses_remaining ?? data.remaining_uses ?? 0;
    const maxFromApi = data.max_uses ?? 0;

    const invitation: InvitationConfig = {
      code: data.code ?? code,
      tier: data.tier || "standard",
      discount: data.discount ?? data.discount_percent,
      expiresAt: validUntil,
      maxUses: maxFromApi > 0 ? maxFromApi : remaining + 1,
      currentUses: maxFromApi > 0 ? maxFromApi - remaining : 0,
    };

    return {
      valid: true,
      invitation,
      layout: data.layout,
      validUntil,
      validForSeconds,
    };
  } catch (error) {
    console.warn("[InvitacionExclusivaArgentina] API error:", error);
    return {
      valid: false,
      message:
        "No se pudo verificar la invitación. Verifica tu conexión e intenta de nuevo.",
    };
  }
}

// Storage key para tracking de uso
const INVITATION_USAGE_KEY = "ar_invitation_usage";
const INVITATION_CLAIMED_KEY = "ar_invitation_claimed";

type InvitationState =
  | "loading"
  | "valid"
  | "expired"
  | "invalid"
  | "used"
  | "claimed";

type CheckoutState = "idle" | "creating" | "redirecting" | "error";

/** Panel de checkout custom (misma página) para mejor UX antes de ir a MercadoPago */
const CUSTOM_CHECKOUT = true;

/**
 * Crea suscripción mensual Stripe (USD). MercadoPago está deshabilitado para
 * membresías; AR pasa por Stripe igual que el resto. Post-pago redirige a
 * `/portal/completar-perfil` (Clerk) → `/pending-approval`.
 */
async function createStripeSubscription(
  invitationCode: string,
  plan: PlanTier,
  buyerEmail: string,
  /** Precio ARS efectivo (admin `ar_invitacion_*_price_ars` con fallback a env/default).
   * Solo alimenta el fallback USD de tracking; el monto real lo define el Price ID. */
  priceArsOverride?: number | null,
): Promise<{ success: true; url: string } | { success: false; error: string }> {
  const { productName } = AR_PLANS[plan];
  const priceArs = priceArsOverride ?? AR_PLANS[plan].price;
  try {
    const base = typeof window !== "undefined" ? window.location.origin : "";
    // Sin priceId — el endpoint resuelve por `plan_tier=vip` desde
    // `membership_products` (admin pega Price ID en /admin/suscripciones).
    // Fallback USD: ARS / 1000 ≈ USD (el Price ID del dashboard manda).
    const fallbackAmountUsdCents = Math.max(100, Math.round((priceArs / 1000) * 100));
    const successUrl =
      `${base}/portal/completar-perfil` +
      `?plan=exclusiva&source=ar_invitacion&invitation_code=${encodeURIComponent(invitationCode)}`;
    const cancelUrl = `${base}/ar/invitacion`;
    const response = await fetch("/api/create-subscription-checkout", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
            ...getDatafastAttributionPayload(),
        amount: fallbackAmountUsdCents,
        currency: "usd",
        productName,
        buyerEmail,
        country: "AR",
        successUrl,
        cancelUrl,
        intervalCount: 1,
        metadata: {
          type: "membresia",
          source: BRAND_SOURCE_KEY,
          invitation_code: invitationCode,
          flow: "ar_invitacion",
          plan_tier: "vip",
          product_type: "exclusive",
          legacy_tier: plan,
        },
      }),
    });

    const data = await response.json();
    if (!response.ok || !data.url) {
      return { success: false, error: data.error || "Error al crear checkout" };
    }
    return { success: true, url: data.url };
  } catch (error) {
    console.error("[InvitacionExclusivaArgentina] Stripe checkout error:", error);
    return { success: false, error: "Error de conexión. Intenta de nuevo." };
  }
}

export default function InvitacionExclusivaArgentina() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Estados
  const [invitationState, setInvitationState] =
    useState<InvitationState>("loading");
  const [invitation, setInvitation] = useState<InvitationConfig | null>(null);
  const [countdown, setCountdown] = useState({
    hours: 0,
    minutes: 0,
    seconds: 0,
  });
  const [sessionExpiry, setSessionExpiry] = useState<Date | null>(null);
  const [showVideo, setShowVideo] = useState(false);
  /** Layout desde la API validate (logo, imagen de fondo, textos). Una sola petición. */
  const [layoutFromApi, setLayoutFromApi] = useState<
    ValidateAPIResponse["layout"] | null
  >(null);
  /** Mensaje de error cuando valid: false (viene de la API). */
  const [invalidMessage, setInvalidMessage] = useState<string>(
    "El código de invitación no existe o es incorrecto. Verifica que tengas el enlace correcto.",
  );
  /** Estado del checkout de MercadoPago */
  const [checkoutState, setCheckoutState] = useState<CheckoutState>("idle");
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  /** Panel de checkout custom visible (mejor UX: resumen antes de ir a MP) */
  const [showCustomCheckout, setShowCustomCheckout] = useState(false);
  /** Plan elegido para checkout: standard ($29.999) o vip ($49.999) */
  const [checkoutPlan, setCheckoutPlan] = useState<PlanTier>("standard");
  /** Email del comprador (requerido por MercadoPago para auto_recurring) */
  const [checkoutEmail, setCheckoutEmail] = useState("");
  /** Qué video mostrar en el modal: standard o vip (playback ID distinto) */
  const [videoTier, setVideoTier] = useState<PlanTier>("standard");

  /** Precios ARS editables desde admin (`premium_invite_config`). null = usar
   * env/default de AR_PLANS. Fallback preserva el comportamiento actual. */
  const [runtimeArStandardPriceArs, setRuntimeArStandardPriceArs] = useState<number | null>(null);
  const [runtimeArVipPriceArs, setRuntimeArVipPriceArs] = useState<number | null>(null);

  // ── Funnel tracking ────────────────────────────────────────────────────────
  const sessionIdRef = useRef<string | undefined>(undefined);
  if (!sessionIdRef.current && typeof crypto !== "undefined") {
    sessionIdRef.current = crypto.randomUUID();
  }

  const fireFunnelEvent = useCallback(
    (eventName: string, props?: Record<string, unknown>) => {
      if (typeof window === "undefined") return;
      const ph = (
        window as unknown as { posthog?: { get_distinct_id?: () => string } }
      ).posthog;
      mirrorFunnelEvent({
        event_name: eventName,
        posthog_distinct_id: ph?.get_distinct_id?.(),
        session_id: sessionIdRef.current,
        template: "InvitacionExclusivaArgentina",
        template_variant: "exclusive_ar_invite",
        flow_name: "exclusive_ar_invite",
        country: "AR",
        language: "es-AR",
        landing_path:
          typeof window !== "undefined" ? window.location.pathname : undefined,
        props: {
          ...(inviteRef ? { invite_ref: inviteRef } : {}),
          ...(inviteCode ? { invite_code: inviteCode } : {}),
          ...props,
        },
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // Origen de invitación coherente: no mezclar ref con code.
  const inviteRef = searchParams.get("ref")?.trim() || "";
  const inviteCode = searchParams.get("code")?.trim() || searchParams.get("c")?.trim() || "";
  const inviteMode: InvitationValidationMode | null = inviteRef
    ? "ref"
    : inviteCode
      ? "code"
      : null;
  const inviteValue = inviteMode === "ref" ? inviteRef : inviteCode;

  // Page view — fires once on mount, sent to funnel_events + Slack
  const pageViewTrackedRef = useRef(false);
  useEffect(() => {
    if (pageViewTrackedRef.current) return;
    pageViewTrackedRef.current = true;
    fireFunnelEvent("exclusive_ar_invite_view", {
      has_ref: !!inviteRef,
      has_code: !!inviteCode,
      entry_path: typeof window !== "undefined" ? window.location.pathname + window.location.search : undefined,
    });
  }, [fireFunnelEvent, inviteRef, inviteCode]);

  // Una sola petición a validate por carga de página (evitar doble fetch)
  useEffect(() => {
    const validateInvitation = async () => {
      if (!inviteMode || !inviteValue) {
        setInvitationState("invalid");
        return;
      }

      const codeUpper = inviteValue.toUpperCase();
      const adminCode = getArInvitacionAdminCode();

      if (inviteMode === "ref" && typeof window !== "undefined") {
        const trackingKey = `tracked_link_opened:${inviteMode}:${codeUpper}`;
        if (!sessionStorage.getItem(trackingKey)) {
          const safeCapture = (
            window as Window & {
              posthogSafeCapture?: (
                eventName: string,
                properties?: Record<string, unknown>,
              ) => boolean;
            }
          ).posthogSafeCapture;
          safeCapture?.("tracked_link_opened", {
            ref: codeUpper,
            page_path: window.location.pathname,
          });
          sessionStorage.setItem(trackingKey, new Date().toISOString());
        }
      }

      // Código admin: entrada infinita, nunca se marca como reclamada
      if (codeUpper === adminCode) {
        const adminInvitation: InvitationConfig = {
          code: adminCode,
          expiresAt: new Date("2099-12-31T23:59:59"),
          maxUses: 99999,
          currentUses: 0,
          tier: "vip",
        };
        setInvitation(adminInvitation);
        setInvitationState("valid");
        const farExpiry = new Date();
        farExpiry.setFullYear(farExpiry.getFullYear() + 10);
        setSessionExpiry(farExpiry);
        return;
      }

      // Verificar si ya fue reclamada (solo para fallback local)
      const claimedCodes = JSON.parse(
        localStorage.getItem(INVITATION_CLAIMED_KEY) || "[]",
      );
      if (claimedCodes.includes(codeUpper) && !isApiUrlConfigured()) {
        const usageData = JSON.parse(
          localStorage.getItem(INVITATION_USAGE_KEY) || "{}",
        );
        const usage = usageData[codeUpper];
        if (usage && new Date(usage.expiresAt) > new Date()) {
          const fallback = FALLBACK_CODES[codeUpper];
          if (fallback) {
            setInvitation(fallback);
            setSessionExpiry(new Date(usage.expiresAt));
            setInvitationState("claimed");
            return;
          }
        }
        setInvitationState("used");
        setInvalidMessage(
          "Esta invitación ha alcanzado el límite máximo de usos o tu sesión ha expirado.",
        );
        return;
      }

      // Con API configurada: una sola petición a validate
      const result = await validateCodeViaAPI(codeUpper, inviteMode);

      if (result.valid === false) {
        setInvitationState("invalid");
        setInvalidMessage(result.message);
        return;
      }

      // valid: true
      const { invitation: inv, layout, validUntil } = result;

      if (import.meta.env.DEV) {
        console.log("[InvitacionExclusivaArgentina] Valid invitation - Layout data:", {
          hasLayout: !!layout,
          layout,
          layoutKeys: layout ? Object.keys(layout) : null,
          backgroundImageUrl: layout?.background_image_url,
          backgroundImageUrlCamel: layout?.backgroundImageUrl,
          imageUrl: layout?.image_url,
          imageUrlCamel: layout?.imageUrl,
          title: layout?.title,
          subtitle: layout?.subtitle,
          theme: layout?.theme,
          ctaText: layout?.cta_text,
        });
      }

      if (new Date() > validUntil) {
        setInvitationState("expired");
        return;
      }

      if (inv.currentUses >= inv.maxUses) {
        setInvitationState("used");
        setInvalidMessage(
          "Esta invitación ha alcanzado el límite máximo de usos o tu sesión ha expirado.",
        );
        return;
      }

      setInvitation(inv);
      setLayoutFromApi(layout ?? null);
      setSessionExpiry(validUntil);
      setInvitationState("valid");

      // Guardar sesión en storage (para fallback y claimed)
      const expiryIso = validUntil.toISOString();
      const usageData = JSON.parse(
        localStorage.getItem(INVITATION_USAGE_KEY) || "{}",
      );
      usageData[codeUpper] = {
        claimedAt: new Date().toISOString(),
        expiresAt: expiryIso,
      };
      localStorage.setItem(INVITATION_USAGE_KEY, JSON.stringify(usageData));
      if (!claimedCodes.includes(codeUpper)) {
        claimedCodes.push(codeUpper);
        localStorage.setItem(
          INVITATION_CLAIMED_KEY,
          JSON.stringify(claimedCodes),
        );
      }
    };

    validateInvitation();
  }, [inviteMode, inviteValue]);

  // Evitar scroll en body cuando se muestra la invitación válida (mobile)
  useEffect(() => {
    if (invitationState !== "valid" && invitationState !== "claimed") return;
    const origOverflow = document.body.style.overflow;
    const origHeight = document.body.style.height;
    const origPosition = document.body.style.position;
    document.body.style.overflow = "hidden";
    document.body.style.height = "100dvh";
    document.body.style.position = "fixed";
    return () => {
      document.body.style.overflow = origOverflow;
      document.body.style.height = origHeight;
      document.body.style.position = origPosition;
    };
  }, [invitationState]);

  // Countdown de sesión
  useEffect(() => {
    if (!sessionExpiry) return;

    const updateCountdown = () => {
      const now = new Date();
      const diff = sessionExpiry.getTime() - now.getTime();

      if (diff <= 0) {
        setCountdown({ hours: 0, minutes: 0, seconds: 0 });
        setInvitationState("used");
        return;
      }

      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      setCountdown({ hours, minutes, seconds });
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, [sessionExpiry]);

  // Tier colors y labels
  const tierConfig = useMemo(() => {
    switch (invitation?.tier) {
      case "ultra_vip":
        return {
          gradient: "from-yellow-400 via-amber-500 to-orange-600",
          badge: "ULTRA VIP",
          icon: Crown,
          borderColor: "border-yellow-500/50",
          glowColor: "shadow-yellow-500/30",
        };
      case "vip":
        return {
          gradient: "from-purple-500 via-pink-500 to-rose-500",
          badge: "VIP",
          icon: Sparkles,
          borderColor: "border-purple-500/50",
          glowColor: "shadow-purple-500/30",
        };
      default:
        return {
          gradient: "from-blue-500 via-indigo-500 to-purple-500",
          badge: "EXCLUSIVO",
          icon: Gift,
          borderColor: "border-blue-500/50",
          glowColor: "shadow-blue-500/30",
        };
    }
  }, [invitation?.tier]);

  const TierIcon = tierConfig.icon;

  // Handler para iniciar checkout de MercadoPago. Si se abre desde el panel, usa checkoutPlan; si se pasa plan, lo usa.
  const handleCheckout = async (planOverride?: PlanTier) => {
    if (!invitation?.code) return;
    const plan = planOverride ?? checkoutPlan;

    const trimmed = checkoutEmail.trim();
    if (!trimmed) {
      setCheckoutError("Ingresá tu email para continuar.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setCheckoutError("Ingresá un email válido.");
      return;
    }

    setCheckoutState("creating");
    setCheckoutError(null);

    const result = await createStripeSubscription(invitation.code, plan, trimmed, effectiveArPriceForPlan(plan));

    if (result.success === true) {
      fireFunnelEvent("exclusive_ar_invite_checkout_created", {
        plan,
        gateway: "stripe",
        funnel_step: "redirecting_to_stripe",
      });
      setCheckoutState("redirecting");
      // Semilla `premium_checkout_intents.status='started'` para recovery emails.
      void fetch("/api/capture-checkout-email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: trimmed.toLowerCase(),
          plan_tier: "vip",
          flow_name: "exclusive_ar_invite",
          country: "AR",
          provider: "stripe",
          currency: "USD",
        }),
        keepalive: true,
      }).catch(() => { /* fire-and-forget */ });
      window.location.href = result.url;
    } else {
      fireFunnelEvent("exclusive_ar_invite_checkout_error", {
        plan,
        gateway: "stripe",
        error: result.error,
      });
      setCheckoutState("error");
      setCheckoutError(result.error);
    }
  };

  // Hidrata precios ARS desde la config remota del admin. null = usar
  // env/default (AR_PLANS), así el comportamiento sin overrides no cambia.
  useEffect(() => {
    fetchPremiumInvitePublicConfig()
      .then((cfg) => {
        if (!cfg) return;
        if (cfg.ar_invitacion_standard_price_ars != null) {
          setRuntimeArStandardPriceArs(cfg.ar_invitacion_standard_price_ars);
        }
        if (cfg.ar_invitacion_vip_price_ars != null) {
          setRuntimeArVipPriceArs(cfg.ar_invitacion_vip_price_ars);
        }
      })
      .catch(() => {});
  }, []);

  // Precio ARS efectivo por plan (admin override ?? env/default).
  const effectiveArPriceForPlan = (plan: PlanTier): number =>
    plan === "vip"
      ? runtimeArVipPriceArs ?? AR_PLANS.vip.price
      : runtimeArStandardPriceArs ?? AR_PLANS.standard.price;

  const formatArs = (value: number) =>
    new Intl.NumberFormat("es-AR", {
      style: "currency",
      currency: "ARS",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  const formattedPriceStandard = formatArs(effectiveArPriceForPlan("standard"));

  // Logo: layout.imageUrl o layout.image_url (aliases camelCase/snake_case)
  const logoUrl =
    layoutFromApi?.imageUrl?.trim() ||
    layoutFromApi?.image_url?.trim() ||
    (!layoutFromApi ? HEADER_PROFILE_IMAGE_URL : undefined);
  // Background: soporta background_image_url, backgroundImageUrl, background_url, backgroundUrl
  // Priorizar snake_case (viene de la API) sobre camelCase
  const backgroundUrl =
    layoutFromApi?.background_image_url?.trim() ||
    layoutFromApi?.backgroundImageUrl?.trim() ||
    layoutFromApi?.background_url?.trim() ||
    layoutFromApi?.backgroundUrl?.trim();
  const resolvedBackgroundUrl =
    backgroundUrl || BANNER_IMAGE_URL || logoUrl || HEADER_PROFILE_IMAGE_URL;
  const hasLayoutBackground = Boolean(backgroundUrl);
  const theme = layoutFromApi?.theme?.toLowerCase();
  const overlayClass =
    theme === "light"
      ? "bg-white/30"
      : theme === "brand"
        ? "bg-purple-900/40"
        : "bg-black/50";
  const titleText =
    layoutFromApi?.title?.trim() || "Te invito a mi canal exclusivo 💜";
  const subtitleText = layoutFromApi?.subtitle?.trim();
  const messageText = layoutFromApi?.message?.trim();
  const ctaText = layoutFromApi?.cta_text?.trim() || "Unirte a mi canal";
  const ctaUrl =
    layoutFromApi?.ctaUrl?.trim() ||
    layoutFromApi?.cta_url?.trim() ||
    SUBSCRIPTION_URL_AR_FALLBACK;

  // Debug layout (DEV only — was logging full layout payload for every AR invite visit)
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    console.log("[InvitacionExclusivaArgentina] Layout computed values:", {
      hasLayoutFromApi: !!layoutFromApi,
      layoutFromApi,
      backgroundImageUrl_snake: layoutFromApi?.background_image_url,
      backgroundImageUrl_camel: layoutFromApi?.backgroundImageUrl,
      imageUrl_snake: layoutFromApi?.image_url,
      imageUrl_camel: layoutFromApi?.imageUrl,
      ctaUrl_snake: layoutFromApi?.cta_url,
      ctaUrl_camel: layoutFromApi?.ctaUrl,
      backgroundUrl,
      resolvedBackgroundUrl,
      hasLayoutBackground,
      logoUrl,
      theme,
      overlayClass,
      titleText,
      subtitleText,
      messageText,
      ctaText,
      ctaUrl,
    });
  }, [layoutFromApi, backgroundUrl, resolvedBackgroundUrl, hasLayoutBackground, logoUrl, theme, overlayClass, titleText, subtitleText, messageText, ctaText, ctaUrl]);

  // Render estados de error (font iOS) - Mobile First + Centrado
  const renderErrorState = (
    title: string,
    message: string,
    icon: React.ReactNode,
    iconColor: string,
    gradient: string,
  ) => (
    <div
      className="h-dvh max-h-dvh overflow-hidden relative flex items-center justify-center px-4 sm:px-6"
      style={{ fontFamily: INVITATION_FONT_FAMILY }}
    >
      {/* Fondo con gradiente animado */}
      <div className="fixed inset-0 -z-10 bg-black">
        <div className={`absolute inset-0 bg-gradient-to-br ${gradient} opacity-20 animate-pulse`} />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-transparent via-black/50 to-black" />
      </div>

      {/* Contenido - Mobile First con centrado perfecto */}
      <div className="w-full max-w-md mx-auto text-center space-y-5 sm:space-y-6 animate-fade-in">
        {/* Icono con efecto glow */}
        <div className="relative inline-flex justify-center w-full">
          <div className={`absolute inset-0 blur-2xl ${iconColor} opacity-40 animate-pulse`} />
          <div className={`relative w-20 h-20 sm:w-24 sm:h-24 bg-gradient-to-br ${gradient} rounded-3xl flex items-center justify-center shadow-2xl transform hover:scale-105 transition-transform`}>
            <div className="absolute inset-0 bg-white/5 rounded-3xl backdrop-blur-sm" />
            <div className="relative">{icon}</div>
          </div>
        </div>

        {/* Título con degradado - Mobile First */}
        <h1 className={`text-2xl sm:text-3xl md:text-4xl font-bold bg-gradient-to-r ${gradient} bg-clip-text text-transparent px-2`}>
          {title}
        </h1>

        {/* Mensaje - Mobile First */}
        <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-5 sm:p-6 border border-white/10 mx-auto">
          <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
            {message}
          </p>
        </div>

        {/* Botón mejorado - Mobile First (full width en mobile) */}
        <div className="pt-2">
          <button
            onClick={() => navigate("/")}
            className={`w-full px-6 sm:px-8 py-3.5 sm:py-4 bg-gradient-to-r ${gradient} rounded-xl font-semibold text-white shadow-lg hover:shadow-2xl transform hover:scale-105 active:scale-95 transition-all duration-200 min-h-[52px] sm:min-h-[56px] flex items-center justify-center gap-2 text-sm sm:text-base`}
          >
            <span>Volver al inicio</span>
            <ExternalLink className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>
        </div>

        {/* Footer sutil - Mobile First */}
        <p className="text-gray-500 sm:text-gray-600 text-xs sm:text-sm mt-4 sm:mt-6 px-2">
          ¿Necesitas ayuda? Contacta al administrador
        </p>
      </div>
    </div>
  );

  // Estado: Cargando (mismo fondo que la página válida para transición suave)
  if (invitationState === "loading") {
    return (
      <div
        className="h-dvh max-h-dvh overflow-hidden bg-transparent text-white flex items-center justify-center"
        style={{ fontFamily: INVITATION_FONT_FAMILY }}
      >
        <div className="fixed inset-0 -z-10 min-h-dvh bg-gradient-to-br from-gray-900 via-purple-950/95 to-black" />
        <div className="animate-pulse text-center">
          <div className="w-16 h-16 mx-auto mb-4 border-4 border-purple-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-gray-400">Verificando invitación...</p>
        </div>
      </div>
    );
  }

  // Estado: Código inválido (mensaje desde API cuando valid: false)
  if (invitationState === "invalid") {
    return renderErrorState(
      "Invitación no válida",
      invalidMessage,
      <Lock className="w-12 h-12 text-white drop-shadow-lg" />,
      "bg-red-500/50",
      "from-red-500 via-pink-500 to-rose-500",
    );
  }

  // Estado: Expirada
  if (invitationState === "expired") {
    return renderErrorState(
      "Invitación expirada",
      "Esta invitación ha expirado y ya no está disponible. Contacta para obtener una nueva.",
      <Clock className="w-12 h-12 text-white drop-shadow-lg" />,
      "bg-yellow-500/50",
      "from-yellow-400 via-orange-400 to-amber-500",
    );
  }

  // Estado: Límite alcanzado / sesión expirada (mensaje desde API o default)
  if (invitationState === "used") {
    return renderErrorState(
      "Límite alcanzado",
      invalidMessage,
      <AlertCircle className="w-12 h-12 text-white drop-shadow-lg" />,
      "bg-orange-500/50",
      "from-orange-500 via-red-500 to-pink-500",
    );
  }

  // Estado: Válida o Reclamada (mostrar contenido)
  // Layout: no scroll, todo en viewport; mobile-first compacto; safe-area para notches
  return (
    <div
      className="h-dvh max-h-dvh overflow-hidden bg-transparent text-white min-w-0 w-full flex flex-col pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] pl-[env(safe-area-inset-left)] pr-[env(safe-area-inset-right)]"
      style={{ fontFamily: INVITATION_FONT_FAMILY }}
    >
      {/* Video Modal - playback según opción (standard o VIP) */}
      {showVideo && (
        <div className="fixed inset-0 z-50 bg-black w-full h-dvh min-h-dvh overflow-hidden">
          <MuxVideoPlayerWithCTA
            countryCode="AR"
            language="es"
            playbackId={videoTier === "vip" ? getChannelInviteVipPlaybackIdForAR() : getChannelInvitePlaybackIdForAR()}
            ctaUrl={ctaUrl}
            ctaLabel={ctaText}
            onClose={() => setShowVideo(false)}
          />
        </div>
      )}

      {/* Checkout custom - panel en la misma página (mejor UX) con background visible */}
      {CUSTOM_CHECKOUT && showCustomCheckout && (
        <div className="fixed inset-0 z-40 flex flex-col overflow-hidden">
          {/* Background del checkout - misma imagen que la página principal */}
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `url(${resolvedBackgroundUrl})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
            }}
          />
          {/* Overlay oscuro con blur */}
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
          {/* Contenido por encima del background */}
          <div className="relative z-10 flex-1 min-h-0 overflow-auto flex flex-col items-center justify-center p-4 max-w-md mx-auto w-full">
            <div className="w-full space-y-4 animate-fade-in">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-bold text-white">Checkout</h2>
                <button
                  type="button"
                  onClick={() => {
                    fireFunnelEvent("exclusive_ar_invite_checkout_abandoned", {
                      plan: checkoutPlan,
                      via: "close_x",
                      had_email: checkoutEmail.trim().length > 0,
                    });
                    setShowCustomCheckout(false);
                    setCheckoutError(null);
                    setCheckoutEmail("");
                  }}
                  className="p-2 rounded-full text-gray-400 hover:text-white hover:bg-white/10 transition-colors"
                  aria-label="Cerrar"
                >
                  <span className="sr-only">Cerrar</span>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className={`rounded-2xl p-4 bg-white/5 border border-white/10`}>
                <div className="flex justify-between items-start mb-3">
                  <span className="text-gray-400 text-sm">Producto</span>
                  <span className="text-white font-medium text-right text-sm">{AR_PLANS[checkoutPlan].productName}</span>
                </div>
                <div className="flex justify-between items-center pt-3 border-t border-white/10">
                  <span className="text-gray-400 text-sm">Precio</span>
                  <span className="text-white font-bold">
                    {formatArs(effectiveArPriceForPlan(checkoutPlan))} <span className="text-gray-400 font-normal text-sm">/ mes</span>
                  </span>
                </div>
                <p className="text-gray-500 text-xs mt-2">
                  Suscripción mensual. Podés cancelar cuando quieras.
                </p>
              </div>

              <div>
                <label htmlFor="checkout-email" className="block text-gray-400 text-sm mb-1">Tu email</label>
                <input
                  id="checkout-email"
                  type="email"
                  placeholder="tu@email.com"
                  value={checkoutEmail}
                  onChange={(e) => {
                    setCheckoutEmail(e.target.value);
                    setCheckoutError(null);
                  }}
                  disabled={checkoutState === "creating" || checkoutState === "redirecting"}
                  className="w-full rounded-xl border border-white/20 bg-white/10 px-4 py-3 text-white placeholder:text-gray-500 focus:border-white/40 focus:outline-none"
                />
              </div>

              {checkoutError && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-3">
                  <p className="text-red-400 text-sm">{checkoutError}</p>
                  <button
                    type="button"
                    onClick={() => setCheckoutError(null)}
                    className="text-white/60 text-xs underline mt-1"
                  >
                    Cerrar
                  </button>
                </div>
              )}

              <button
                onClick={() => {
                  fireFunnelEvent("exclusive_ar_invite_checkout_submit", {
                    plan: checkoutPlan,
                    gateway: "mercadopago",
                    has_email: checkoutEmail.trim().includes("@"),
                  });
                  handleCheckout();
                }}
                disabled={checkoutState === "creating" || checkoutState === "redirecting"}
                className={`w-full py-4 px-4 bg-gradient-to-r ${tierConfig.gradient} rounded-xl font-bold text-white shadow-lg active:scale-[0.98] flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed transition-all`}
              >
                {checkoutState === "creating" ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Preparando pago...
                  </>
                ) : checkoutState === "redirecting" ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Redirigiendo a Mercado Pago...
                  </>
                ) : (
                  <>
                    <span>Pagar con Mercado Pago</span>
                    <ExternalLink className="w-4 h-4 opacity-80" />
                  </>
                )}
              </button>

              <button
                type="button"
                onClick={() => {
                  fireFunnelEvent("exclusive_ar_invite_checkout_abandoned", {
                    plan: checkoutPlan,
                    via: "volver_button",
                    had_email: checkoutEmail.trim().length > 0,
                  });
                  setShowCustomCheckout(false);
                  setCheckoutError(null);
                  setCheckoutEmail("");
                }}
                className="w-full py-3 text-gray-400 hover:text-white text-sm font-medium transition-colors"
              >
                Volver
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Background: z-0 — gradiente + imagen + overlays + capa oscura encima */}
      <div
        className="fixed inset-0 z-0 min-h-dvh w-full"
        style={{ minHeight: "100dvh" }}
        aria-hidden
      >
        {/* Capa 1: gradiente por defecto (fallback si la imagen falla) */}
        <div
          className="absolute inset-0 bg-gradient-to-br from-gray-900 via-purple-950/95 to-black"
          aria-hidden
        />
        {/* Capa 2: imagen de layout o fallback (BANNER / logo) */}
        {resolvedBackgroundUrl && (
          <div
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{
              backgroundImage: `url(${resolvedBackgroundUrl})`,
            }}
            aria-hidden
          />
        )}
        {/* Capa 3: overlays según tema o sin layout */}
        {hasLayoutBackground && (
          <div className={`absolute inset-0 ${overlayClass}`} aria-hidden />
        )}
        {!hasLayoutBackground && (
          <>
            <div className="absolute inset-0 bg-black/35" aria-hidden />
            <div
              className={`absolute inset-0 bg-gradient-to-br ${tierConfig.gradient} opacity-20`}
              aria-hidden
            />
          </>
        )}
        {/* Capa 4: oscura SIEMPRE encima de la imagen — legibilidad */}
        {resolvedBackgroundUrl && (
          <div className="absolute inset-0 bg-black/55" aria-hidden />
        )}
      </div>

      {/* Main Content: mobile-first, centrado vertical y horizontal (flex-col + items-center + justify-center) */}
      <div className="relative z-10 flex-1 min-h-0 w-full min-w-0 flex flex-col items-center justify-center overflow-hidden px-3 py-2 sm:px-4 sm:py-4">
        <div
          className="w-full max-w-md max-h-[calc(100dvh-2rem)] mx-auto overflow-y-auto overflow-x-hidden text-center shrink-0 flex flex-col items-center justify-center py-2"
          style={{ marginLeft: "auto", marginRight: "auto" }}
        >
          {/* Badge — compacto en mobile */}
          <div className="flex justify-center mb-1.5 sm:mb-3">
            <div
              className={`inline-flex items-center gap-1.5 bg-gradient-to-r ${tierConfig.gradient} px-3 py-1 rounded-full`}
            >
              <TierIcon className="w-3 h-3 sm:w-4 sm:h-4 text-white" />
              <span className="text-[10px] sm:text-xs font-bold tracking-wider text-white">
                {tierConfig.badge}
              </span>
            </div>
          </div>

          {/* Logo — más chico en mobile */}
          <div className="text-center mb-1.5 sm:mb-3">
            {logoUrl && (
              <div className="relative inline-block mb-1 sm:mb-2">
                <div
                  className={`absolute -inset-0.5 sm:-inset-1 bg-gradient-to-r ${tierConfig.gradient} rounded-full blur opacity-50`}
                />
                <img
                  src={logoUrl}
                  alt=""
                  className="relative w-12 h-12 sm:w-16 sm:h-16 md:w-20 md:h-20 rounded-full object-cover"
                />
                <div className="absolute -bottom-0.5 -right-0.5 bg-green-500 w-3 h-3 sm:w-4 sm:h-4 rounded-full border-2 border-black flex items-center justify-center">
                  <CheckCircle className="w-1.5 h-1.5 sm:w-2.5 sm:h-2.5 text-white" />
                </div>
              </div>
            )}
            <h1 className="text-sm sm:text-lg font-bold mb-0">{BRAND_PERSON_NAME}</h1>
            <p className="text-gray-400 text-[10px] sm:text-xs">🇦🇷 Argentina</p>
          </div>

          {/* Código + expiración — card compacta */}
          <div className="w-full rounded-xl p-2.5 sm:p-3 mb-1.5 sm:mb-3 bg-white/5 backdrop-blur-sm">
            <div className="flex items-center justify-between gap-2">
              <span className="text-[10px] sm:text-sm text-gray-400 shrink-0">
                Código
              </span>
              <span className="font-body font-bold text-white text-xs sm:text-sm truncate tabular-nums tracking-wide">
                {invitation?.code}
              </span>
            </div>
            {sessionExpiry && (
              <div className="flex items-center gap-1.5 mt-2 pt-2 border-t border-white/10">
                <Clock className="w-3 h-3 text-purple-400 flex-shrink-0" />
                <span className="text-[10px] sm:text-sm text-white/70">
                  Expira en:
                </span>
                <span className="font-body font-bold text-white text-xs sm:text-sm ml-auto tabular-nums tracking-tight">
                  {String(countdown.hours).padStart(2, "0")}:
                  {String(countdown.minutes).padStart(2, "0")}:
                  {String(countdown.seconds).padStart(2, "0")}
                </span>
              </div>
            )}
            {layoutFromApi?.spots_remaining != null && layoutFromApi?.max_spots != null && (
              <div className="flex items-center justify-between mt-1.5">
                <span className="text-[10px] sm:text-sm text-gray-400">Lugares</span>
                <span className="font-bold text-white text-xs sm:text-sm">{layoutFromApi.spots_remaining} / {layoutFromApi.max_spots}</span>
              </div>
            )}
          </div>

          {/* Mensaje — 1–2 líneas en mobile */}
          <div className="text-center mb-1.5 sm:mb-3 w-full">
            <h2 className="text-sm sm:text-base font-semibold mb-0.5 line-clamp-1 sm:line-clamp-none">
              {titleText}
            </h2>
            {subtitleText && (
              <p className="text-gray-400 text-[10px] sm:text-xs mb-0.5 line-clamp-1">{subtitleText}</p>
            )}
            {messageText && (
              <p className="text-gray-400 text-[10px] sm:text-xs leading-snug line-clamp-2 sm:line-clamp-3">{messageText}</p>
            )}
          </div>

          {/* CTAs — dos opciones compactas */}
          <div className="w-full space-y-2">
            <p className="text-center text-gray-400 text-[10px] sm:text-xs mb-1">Elegí tu acceso</p>

            {/* Standard */}
            <div className="rounded-xl p-2.5 sm:p-3 bg-white/5 border border-white/10">
              <div className="flex items-center justify-between gap-2 mb-1">
                <span className="text-white font-semibold text-xs sm:text-sm">Canal Exclusivo</span>
                <span className="text-base sm:text-lg font-bold text-white shrink-0">{formattedPriceStandard}</span>
              </div>
              <p className="text-gray-400 text-[10px] mb-1.5">{AR_PLAN_STANDARD_SUBTITLE}</p>
              <div className="flex gap-1.5">
                <button
                  onClick={() => {
                    setCheckoutPlan("standard");
                    fireFunnelEvent("exclusive_ar_invite_checkout_open", {
                      plan: "standard",
                      mode: CUSTOM_CHECKOUT ? "custom_panel" : "direct",
                    });
                    if (CUSTOM_CHECKOUT) setShowCustomCheckout(true);
                    else handleCheckout("standard");
                  }}
                  disabled={!CUSTOM_CHECKOUT && (checkoutState === "creating" || checkoutState === "redirecting")}
                  className={`flex-1 py-2 px-2.5 sm:py-2.5 sm:px-3 bg-gradient-to-r ${tierConfig.gradient} rounded-lg font-bold text-white text-xs sm:text-sm min-h-[38px] sm:min-h-[42px] flex items-center justify-center gap-1.5 disabled:opacity-70 disabled:cursor-not-allowed transition-all`}
                >
                  {!CUSTOM_CHECKOUT && checkoutState === "creating" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : ctaText}
                </button>
                <button
                  onClick={() => {
                    fireFunnelEvent("exclusive_ar_invite_video_click", { plan: "standard" });
                    setVideoTier("standard");
                    setShowVideo(true);
                  }}
                  className="py-2 px-2.5 sm:py-2.5 sm:px-3 bg-white/10 hover:bg-white/20 rounded-lg font-semibold text-white text-[10px] sm:text-xs min-h-[38px] sm:min-h-[42px]"
                >
                  Ver video
                </button>
              </div>
            </div>

            {/* VIP: solo botón de acceso, sin precio ni ver video */}
            <div className={`rounded-xl p-2.5 sm:p-3 border ${
              !isVipPlanCardAvailable()
                ? "bg-gray-900/20 border-gray-600/20 opacity-60"
                : "bg-gradient-to-r from-purple-500/10 to-pink-500/10 border-purple-500/30"
            }`}>
              <div className="flex items-center gap-1.5 sm:gap-2 mb-1">
                <span className={`font-semibold text-[11px] sm:text-xs inline-flex items-center gap-1 ${
                  !isVipPlanCardAvailable() ? "text-gray-500" : "text-white"
                }`}>
                  <Crown className={`w-3 h-3 sm:w-3.5 sm:h-3.5 flex-shrink-0 ${
                    !isVipPlanCardAvailable() ? "text-gray-600" : "text-amber-400"
                  }`} />
                  VIP
                </span>
                {!isVipPlanCardAvailable() && (
                  <span className="ml-auto text-[8px] sm:text-[9px] font-bold tracking-wide text-gray-500 uppercase bg-gray-700/50 px-1.5 sm:px-2 py-0.5 rounded-full flex items-center gap-0.5 whitespace-nowrap">
                    <Lock className="w-2 h-2 sm:w-2.5 sm:h-2.5" />
                    Completo
                  </span>
                )}
              </div>
              <p className={`text-[9px] sm:text-[10px] mb-1.5 leading-tight ${
                !isVipPlanCardAvailable() ? "text-gray-600" : "text-gray-400"
              }`}>{AR_PLAN_VIP_SUBTITLE}</p>
              <button
                onClick={() => {
                  if (!isVipPlanCardAvailable()) return;
                  fireFunnelEvent("exclusive_ar_invite_vip_click", {
                    destination: AR_VIP_EXCLUSIVE_PATH,
                  });
                  navigate(AR_VIP_EXCLUSIVE_PATH);
                }}
                disabled={!isVipPlanCardAvailable()}
                className={`w-full py-2 sm:py-2.5 px-2 sm:px-3 rounded-lg font-bold text-[10px] sm:text-xs min-h-[36px] sm:min-h-[38px] flex items-center justify-center transition-all leading-tight ${
                  !isVipPlanCardAvailable()
                    ? "bg-gray-700/30 text-gray-600 cursor-not-allowed"
                    : "bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:opacity-95 active:scale-[0.98]"
                }`}
              >
                {!isVipPlanCardAvailable() ? "No disponible" : "Unirme VIP"}
              </button>
            </div>

            {!CUSTOM_CHECKOUT && checkoutState === "error" && checkoutError && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-2 text-center">
                <p className="text-red-400 text-[10px] sm:text-xs">{checkoutError}</p>
                <button onClick={() => { setCheckoutState("idle"); setCheckoutError(null); }} className="text-white/60 text-[10px] underline mt-0.5">Reintentar</button>
              </div>
            )}

            <p className="text-center text-gray-500 text-[10px] mt-0.5">Suscripción mensual · Redirect a Telegram</p>
          </div>

          {/* Trust + footer en una línea compacta */}
          <div className="mt-1.5 sm:mt-3 flex items-center justify-center gap-2 sm:gap-3 text-[10px] sm:text-xs text-gray-500 flex-wrap">
            <span className="inline-flex items-center gap-0.5"><Lock className="w-2.5 h-2.5" /> Pago seguro</span>
            <span className="w-1 h-1 bg-gray-600 rounded-full" />
            <span className="inline-flex items-center gap-0.5"><CheckCircle className="w-2.5 h-2.5" /> Acceso inmediato</span>
            <span className="w-1 h-1 bg-gray-600 rounded-full hidden sm:inline" />
            <span className="text-gray-600/80">Código: {inviteValue.toUpperCase()}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
